import { zodResolver } from "@hookform/resolvers/zod";
import { Chip, Spinner } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { useEffect, useMemo, useRef } from "react";

import {
  CheckboxField,
  SideSheet,
  TextInputField,
  notify,
} from "@/components/kit";
import { MultiPicker } from "@/components/seo/multi-picker";
import {
  OptionalNumberField,
  OptionalSelectField,
  TextAreaField,
} from "@/components/seo/fields";
import { useAddSeoSlug } from "@/hooks/api/seo-slug/useAddSeoSlug";
import { useUpdateSeoSlug } from "@/hooks/api/seo-slug/useUpdateSeoSlug";
import { useGetAllBrands } from "@/hooks/api/brand/useGetAllBrands";
import { useGetAllCategories } from "@/hooks/api/category/useGetAllCategories";
import { useGetAllCountries } from "@/hooks/api/country/useGetAllCountries";
import { ProductRepository } from "@/repositories/product";
import { ProductFilters } from "@/types";
import {
  AddSeoSlug,
  SeoSlug,
  SeoSlugFilters,
  UpdateSeoSlug,
} from "@/types/seo-slug";

interface AddEditSeoSlugModalProps {
  isOpen: boolean;
  onClose: () => void;
  isEdit: boolean;
  seoSlug?: SeoSlug;
}

const SEO_SLUG_SCHEMA = z.object({
  id: z.string().optional(),
  slug: z
    .string()
    .trim()
    .min(1, "English slug is required")
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Slug must be lowercase with hyphens only",
    ),
  slugAr: z.string().trim().min(1, "Arabic slug is required"),
  metaTitle: z.string().trim().min(1, "English meta title is required"),
  metaTitleAr: z.string().trim().min(1, "Arabic meta title is required"),
  metaDescription: z
    .string()
    .trim()
    .min(1, "English meta description is required"),
  metaDescriptionAr: z
    .string()
    .trim()
    .min(1, "Arabic meta description is required"),
  content: z.string().optional(),
  contentAr: z.string().optional(),
  isActive: z.boolean(),
  filterCategories: z.array(z.string()).optional(),
  filterSubCategories: z.array(z.string()).optional(),
  filterBrands: z.array(z.string()).optional(),
  filterCountries: z.array(z.string()).optional(),
  filterTags: z.string().optional(),
  filterPriceFrom: z.number().optional(),
  filterPriceTo: z.number().optional(),
  filterSortBy: z.string().optional(),
  filterSortDirection: z.string().optional(),
});

type SeoSlugFormData = z.infer<typeof SEO_SLUG_SCHEMA>;

const DEFAULT_VALUES: SeoSlugFormData = {
  id: "",
  slug: "",
  slugAr: "",
  metaTitle: "",
  metaTitleAr: "",
  metaDescription: "",
  metaDescriptionAr: "",
  content: "",
  contentAr: "",
  isActive: true,
  filterCategories: [],
  filterSubCategories: [],
  filterBrands: [],
  filterCountries: [],
  filterTags: "",
  filterPriceFrom: undefined,
  filterPriceTo: undefined,
  filterSortBy: "",
  filterSortDirection: "",
};

const buildFilters = (values: SeoSlugFormData): SeoSlugFilters | undefined => {
  const filters: SeoSlugFilters = {};
  let hasFilter = false;

  if (values.filterCategories?.length) {
    filters.categories = values.filterCategories;
    hasFilter = true;
  }
  if (values.filterSubCategories?.length) {
    filters.subCategories = values.filterSubCategories;
    hasFilter = true;
  }
  if (values.filterBrands?.length) {
    filters.brands = values.filterBrands;
    hasFilter = true;
  }
  if (values.filterCountries?.length) {
    filters.countries = values.filterCountries;
    hasFilter = true;
  }
  if (values.filterTags?.trim()) {
    filters.tags = values.filterTags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    hasFilter = true;
  }
  if (values.filterPriceFrom != null) {
    filters.priceFrom = values.filterPriceFrom;
    hasFilter = true;
  }
  if (values.filterPriceTo != null) {
    filters.priceTo = values.filterPriceTo;
    hasFilter = true;
  }
  if (values.filterSortBy) {
    filters.sortBy = values.filterSortBy as SeoSlugFilters["sortBy"];
    hasFilter = true;
  }
  if (values.filterSortDirection) {
    filters.sortDirection =
      values.filterSortDirection as SeoSlugFilters["sortDirection"];
    hasFilter = true;
  }

  return hasFilter ? filters : undefined;
};

const filtersToFormValues = (
  filters?: SeoSlugFilters,
): Partial<SeoSlugFormData> => {
  if (!filters) return {};

  return {
    filterCategories: filters.categories ?? [],
    filterSubCategories: filters.subCategories ?? [],
    filterBrands: filters.brands ?? [],
    filterCountries: filters.countries ?? [],
    filterTags: filters.tags?.join(", ") ?? "",
    filterPriceFrom: filters.priceFrom,
    filterPriceTo: filters.priceTo,
    filterSortBy: filters.sortBy ?? "",
    filterSortDirection: filters.sortDirection ?? "",
  };
};

const SORT_BY_OPTIONS = [
  { id: "price", name: "Price" },
  { id: "featured", name: "Featured" },
  { id: "order", name: "Order" },
];

const SORT_DIR_OPTIONS = [
  { id: "asc", name: "Ascending" },
  { id: "desc", name: "Descending" },
];

export const AddEditSeoSlugModal = ({
  isOpen,
  onClose,
  isEdit,
  seoSlug,
}: AddEditSeoSlugModalProps) => {
  const form = useForm<SeoSlugFormData>({
    resolver: zodResolver(SEO_SLUG_SCHEMA),
    defaultValues: DEFAULT_VALUES,
  });
  const { handleSubmit, control, reset, watch } = form;

  const { data: brands, isLoading: isBrandsLoading } = useGetAllBrands();
  const { data: categories, isLoading: isCategoriesLoading } =
    useGetAllCategories();
  const { data: countries, isLoading: isCountriesLoading } =
    useGetAllCountries();

  const productRepository = useRef(new ProductRepository());

  const selectedCategories = watch("filterCategories");
  const selectedSubCategories = watch("filterSubCategories");
  const selectedBrands = watch("filterBrands");
  const selectedCountries = watch("filterCountries");
  const watchedTags = watch("filterTags");
  const watchedPriceFrom = watch("filterPriceFrom");
  const watchedPriceTo = watch("filterPriceTo");
  const watchedSortBy = watch("filterSortBy");
  const watchedSortDirection = watch("filterSortDirection");

  const productFilterParams = useMemo((): ProductFilters => {
    const params: ProductFilters = { page: 1, limit: 1 };

    if (selectedCategories?.length) params.categories = selectedCategories;
    if (selectedSubCategories?.length)
      params.subCategories = selectedSubCategories;
    if (selectedBrands?.length) params.brands = selectedBrands;
    if (selectedCountries?.length) params.countries = selectedCountries;
    if (watchedTags?.trim())
      params.tags = watchedTags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
    if (watchedPriceFrom != null) params.priceFrom = watchedPriceFrom;
    if (watchedPriceTo != null) params.priceTo = watchedPriceTo;
    if (watchedSortBy && watchedSortDirection)
      params.sort = [
        {
          key: watchedSortBy,
          direction: watchedSortDirection as "asc" | "desc",
        },
      ];

    return params;
  }, [
    selectedCategories,
    selectedSubCategories,
    selectedBrands,
    selectedCountries,
    watchedTags,
    watchedPriceFrom,
    watchedPriceTo,
    watchedSortBy,
    watchedSortDirection,
  ]);

  const hasAnyFilter =
    (selectedCategories?.length ?? 0) > 0 ||
    (selectedSubCategories?.length ?? 0) > 0 ||
    (selectedBrands?.length ?? 0) > 0 ||
    (selectedCountries?.length ?? 0) > 0 ||
    !!watchedTags?.trim() ||
    watchedPriceFrom != null ||
    watchedPriceTo != null;

  const { data: productPreview, isFetching: isPreviewLoading } = useQuery({
    queryKey: ["seo-slug-product-preview", productFilterParams],
    queryFn: () => productRepository.current.findAll(productFilterParams),
    enabled: hasAnyFilter && isOpen,
  });

  const categoryOptions = useMemo(
    () => categories?.map((c) => ({ id: c.id, name: c.name })) ?? [],
    [categories],
  );

  const subCategoryOptions = useMemo(() => {
    if (!categories || !selectedCategories?.length) return [];

    return categories
      .filter((cat) => selectedCategories.includes(cat.id))
      .flatMap((cat) =>
        cat.subCategories.map((sub) => ({
          id: sub.id,
          name: `${cat.name} › ${sub.name}`,
        })),
      );
  }, [categories, selectedCategories]);

  const brandOptions = useMemo(
    () => brands?.map((b) => ({ id: b.id, name: b.name })) ?? [],
    [brands],
  );

  const countryOptions = useMemo(
    () => countries?.map((c) => ({ id: c.id, name: c.name })) ?? [],
    [countries],
  );

  useEffect(() => {
    if (isOpen && seoSlug && isEdit) {
      reset({
        id: seoSlug.id,
        slug: seoSlug.slug,
        slugAr: seoSlug.slugAr,
        metaTitle: seoSlug.metaTitle,
        metaTitleAr: seoSlug.metaTitleAr,
        metaDescription: seoSlug.metaDescription,
        metaDescriptionAr: seoSlug.metaDescriptionAr,
        content: seoSlug.content ?? "",
        contentAr: seoSlug.contentAr ?? "",
        isActive: seoSlug.isActive,
        ...filtersToFormValues(seoSlug.filters),
      });
    } else if (isOpen && !isEdit) {
      reset(DEFAULT_VALUES);
    }
  }, [isOpen, seoSlug, isEdit, reset]);

  const { mutate: addSeoSlug, isPending: isAdding } = useAddSeoSlug();
  const { mutate: updateSeoSlug, isPending: isUpdating } = useUpdateSeoSlug();

  const onSubmitForm = (values: SeoSlugFormData) => {
    const filters = buildFilters(values);

    if (isEdit) {
      const payload: UpdateSeoSlug = {
        id: values.id!,
        slug: values.slug,
        slugAr: values.slugAr,
        metaTitle: values.metaTitle,
        metaTitleAr: values.metaTitleAr,
        metaDescription: values.metaDescription,
        metaDescriptionAr: values.metaDescriptionAr,
        content: values.content || undefined,
        contentAr: values.contentAr || undefined,
        filters: filters,
        isActive: values.isActive,
      };

      updateSeoSlug(payload, {
        onSuccess: () => {
          notify.success("SEO slug updated successfully");
          handleClose();
        },
        onError: (error: any) => {
          notify.error(
            error.message,
            error.response?.data?.message || "Failed to update SEO slug",
          );
        },
      });
    } else {
      const payload: AddSeoSlug = {
        slug: values.slug,
        slugAr: values.slugAr,
        metaTitle: values.metaTitle,
        metaTitleAr: values.metaTitleAr,
        metaDescription: values.metaDescription,
        metaDescriptionAr: values.metaDescriptionAr,
        content: values.content || undefined,
        contentAr: values.contentAr || undefined,
        filters: filters,
        isActive: values.isActive,
      };

      addSeoSlug(payload, {
        onSuccess: () => {
          notify.success("SEO slug added successfully");
          handleClose();
        },
        onError: (error: any) => {
          notify.error(
            error.message,
            error.response?.data?.message || "Failed to add SEO slug",
          );
        },
      });
    }
  };

  const handleClose = () => {
    reset(DEFAULT_VALUES);
    onClose();
  };

  const pick = (options: Array<{ id: string; name: string }>, ids?: string[]) =>
    (ids ?? []).map((id) => ({
      id,
      name: options.find((option) => option.id === id)?.name ?? id,
    }));

  return (
    <SideSheet
      isOpen={isOpen}
      isSubmitting={isAdding || isUpdating}
      submitLabel={isEdit ? "Update" : "Save"}
      title={isEdit ? "Edit SEO Slug" : "Add SEO Slug"}
      onClose={handleClose}
      onSubmit={() => handleSubmit(onSubmitForm)()}
    >
      <FormProvider {...form}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextInputField<SeoSlugFormData>
            isMono
            isRequired
            label="Slug (English)"
            name="slug"
            placeholder="e.g. organic-honey"
          />
          <TextInputField<SeoSlugFormData>
            isRequired
            dir="rtl"
            label="Slug (Arabic)"
            name="slugAr"
            placeholder="e.g. عسل-طبيعي"
          />
          <TextInputField<SeoSlugFormData>
            isRequired
            label="Meta Title (English)"
            name="metaTitle"
            placeholder="Enter meta title"
          />
          <TextInputField<SeoSlugFormData>
            isRequired
            dir="rtl"
            label="Meta Title (Arabic)"
            name="metaTitleAr"
            placeholder="أدخل عنوان الميتا"
          />
          <TextAreaField<SeoSlugFormData>
            isRequired
            label="Meta Description (English)"
            name="metaDescription"
            placeholder="Enter meta description"
            rows={3}
          />
          <TextAreaField<SeoSlugFormData>
            isRequired
            dir="rtl"
            label="Meta Description (Arabic)"
            name="metaDescriptionAr"
            placeholder="أدخل وصف الميتا"
            rows={3}
          />
          <TextAreaField<SeoSlugFormData>
            label="Content (English)"
            name="content"
            placeholder="Optional page content"
            rows={4}
          />
          <TextAreaField<SeoSlugFormData>
            dir="rtl"
            label="Content (Arabic)"
            name="contentAr"
            placeholder="محتوى الصفحة الاختياري"
            rows={4}
          />
        </div>

        <div className="mt-2 flex items-center gap-2">
          <h3 className="text-muted text-sm">Product filters</h3>
          {hasAnyFilter ? (
            isPreviewLoading ? (
              <Spinner size="sm" />
            ) : (
              <Chip color="accent" variant="soft">
                {productPreview?.total ?? 0} products match
              </Chip>
            )
          ) : null}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Controller
            control={control}
            name="filterCategories"
            render={({ field }) => (
              <MultiPicker
                isLoading={isCategoriesLoading}
                label="Categories"
                options={categoryOptions}
                placeholder="Search & select categories"
                selected={pick(categoryOptions, field.value)}
                onChange={(next) => field.onChange(next.map((o) => o.id))}
              />
            )}
          />
          <Controller
            control={control}
            name="filterSubCategories"
            render={({ field }) => (
              <MultiPicker
                isLoading={isCategoriesLoading}
                label="Sub categories"
                options={subCategoryOptions}
                placeholder="Search & select sub categories"
                selected={pick(subCategoryOptions, field.value)}
                onChange={(next) => field.onChange(next.map((o) => o.id))}
              />
            )}
          />
          <Controller
            control={control}
            name="filterBrands"
            render={({ field }) => (
              <MultiPicker
                isLoading={isBrandsLoading}
                label="Brands"
                options={brandOptions}
                placeholder="Search & select brands"
                selected={pick(brandOptions, field.value)}
                onChange={(next) => field.onChange(next.map((o) => o.id))}
              />
            )}
          />
          <Controller
            control={control}
            name="filterCountries"
            render={({ field }) => (
              <MultiPicker
                isLoading={isCountriesLoading}
                label="Countries"
                options={countryOptions}
                placeholder="Search & select countries"
                selected={pick(countryOptions, field.value)}
                onChange={(next) => field.onChange(next.map((o) => o.id))}
              />
            )}
          />
        </div>

        <TextInputField<SeoSlugFormData>
          description="Comma-separated tags"
          label="Tags"
          name="filterTags"
          placeholder="e.g. organic, natural, premium"
        />

        <div className="grid grid-cols-2 gap-4">
          <OptionalNumberField<SeoSlugFormData>
            label="Min Price (AED)"
            name="filterPriceFrom"
            placeholder="Enter minimum price"
          />
          <OptionalNumberField<SeoSlugFormData>
            label="Max Price (AED)"
            name="filterPriceTo"
            placeholder="Enter maximum price"
          />
          <OptionalSelectField<SeoSlugFormData>
            label="Sort By"
            name="filterSortBy"
            options={SORT_BY_OPTIONS}
            placeholder="Select sort field"
          />
          <OptionalSelectField<SeoSlugFormData>
            label="Sort Direction"
            name="filterSortDirection"
            options={SORT_DIR_OPTIONS}
            placeholder="Select direction"
          />
        </div>

        <CheckboxField<SeoSlugFormData> label="Active" name="isActive" />
      </FormProvider>
    </SideSheet>
  );
};
